import React, { useState } from 'react';
import cx from 'classnames';
import { observer } from 'mobx-react-lite';
import { Modal } from '@JohnLewisPartnership/jl-design-system/dist/components/modal/Modal';
import { PrimaryButton, UnderlinedButton } from '@JohnLewisPartnership/jl-design-system/dist/elements/button/Button';
import { WishListRemove } from './ModalContent/WishListRemove';
import { WishListSelector } from './ModalContent/WishListSelector';
import { WishListVariants } from './ModalContent/WishListVariants';
import WishListsStore from '../../stores/WishListsStore';
import styles from './WishListModal.module.scss';
import { WISHLIST_URL_WITH_CREATE_WL_MODAL } from '../../helpers/redirect/constants';

interface WishlistModalProps {
    wishListStore: WishListsStore;
}

export const WishListModal: React.FC<WishlistModalProps> = observer(({ wishListStore }) => {
    const [store] = useState(wishListStore);
    const { currentModal, reset } = store;

    let header = '';
    switch (currentModal) {
        case 'wishlist':
            header = 'Select a list';
            break;
        case 'variant':
            // eslint-disable-next-line no-case-declarations
            const hasSizes = (store.currentProduct?.sizes?.length ?? 0) > 0;
            // eslint-disable-next-line no-case-declarations
            const hasColours = (store.currentProduct?.colours?.length ?? 0) > 0;

            if (hasColours && hasSizes) {
                header = 'Confirm size and colour';
            } else {
                if (hasSizes) {
                    header = 'Confirm size';
                } else {
                    header = 'Confirm colour';
                }
            }
            break;
        case 'remove':
            header = 'Remove all variations from your lists?';
            break;
    }

    function onBack(): void {
        store.setModal('variant');
    }

    function onClose(): void {
        reset();
    }
    async function onConfirm(): Promise<void> {
        store.dispatchGlobalEvent(
            'add.wishlist',
            store.getActionType(),
            'add to wishlist',
            store.currentProduct,
            'select a list',
        );
        for (const wishListId of store.selectedWishListIds) {
            await store.addToWishList(store.currentProductSkuId, wishListId);
        }

        if (!store.hasError) {
            if (
                (store.mode === 'icon-placeholder-pdp' || store.mode === 'icon-placeholder-basket') &&
                store.variantChanged
            ) {
                store.updateURL();
            }
            onClose();
        }
    }

    const selectWishListModal = {
        isSelectWishListModal: currentModal === 'wishlist',
        buttonCaption: !store.selectedWishListIds.length ? 'Choose a list first' : 'Confirm',
        buttonProps: {
            disabled: !store.selectedWishListIds.length || store.isCallingAPI,
            submitting: store.isCallingAPI,
        },
    };

    const productMetadata = store.parseCurrentProductMetadata();

    // NOTE: removed usage of modal primary and underline footer buttons due to a fault with v24 of JLDS which prevented the modal closing as the submitting state was not being updated.
    // This was fixed in v24.2.1 but the classes were broken for this repo in v24.2.0, so this is a workaround. Should these both be fixed we could return to using the inbuilt footer buttons.

    return (
        <Modal
            data-testid="wishlist-modal"
            className={cx(styles.modal, currentModal)}
            isOpen={!!currentModal}
            title={header}
            onBackButtonClick={onBack}
            onClose={onClose}
            backButton={
                currentModal == 'wishlist' && store.currentProduct?.isMultiSku && store.mode !== 'icon-placeholder-pdp'
            }
            headerType={currentModal == 'remove' ? 'empty' : 'standard'}
            width="small"
            footer={false}
            footerLayout="stacked"
        >
            {currentModal === 'variant' && <WishListVariants store={store} onClose={onClose} />}

            {selectWishListModal.isSelectWishListModal && <WishListSelector store={store} />}

            {currentModal === 'remove' && <WishListRemove store={store} onClose={onClose} />}

            {selectWishListModal.isSelectWishListModal && (
                <div className={styles.modalFooter} data-testid="modal-footer">
                    <UnderlinedButton
                        data-testid={'modal-underlined-button'}
                        onClick={(): void => {
                            const skuId = store.currentProduct?.skuId;
                            const fabricSku = productMetadata?.asafProperties?.fabricSku;
                            const legType = productMetadata?.asafProperties?.legType;

                            window.location.href = `${WISHLIST_URL_WITH_CREATE_WL_MODAL}${
                                skuId ? `&skuId=${skuId}` : ''
                            }${fabricSku ? `&fabricSku=${fabricSku}` : ''}${legType ? `&legType=${legType}` : ''}`;
                        }}
                    >
                        {selectWishListModal.isSelectWishListModal ? 'Create a new wish list' : ''}
                    </UnderlinedButton>
                    <PrimaryButton
                        data-testid={'modal-primary-button'}
                        onClick={onConfirm}
                        submitting={selectWishListModal.buttonProps.submitting}
                        disabled={selectWishListModal.buttonProps.disabled}
                    >
                        {selectWishListModal.buttonCaption}
                    </PrimaryButton>
                </div>
            )}
        </Modal>
    );
});
